import { React, useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { useTheme } from 'styled-components';
import { Sparkles, Send, LogOut, X } from 'lucide-react';
import PaymentComponent from './PaymentComponent';
import { fetchInstructorPaywallPackages } from './services/instructorPlansPricingApi';
import {
    filterUpgradeablePlans,
    getRecommendedUpgradePlanId,
    sortPlansByTier,
    tierRank,
} from './services/instructorPlanTier';
import { formatPlanLabel, resolvePlanPricingDisplay } from './admin/Packages/planDisplayUtils';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useAppContext } from '../appprovider';
import {
    Sheet,
    Head,
    Chip,
    Title,
    Sub,
    Foot,
    FootLink,
    EmptyState,
    PlanTierGrid,
    PlanTierGridShimmer,
    PlanTierTrustStrip,
    ActivationCodeEntryLink,
    getRecommendedPlanId,
    planTierDialogPaperProps,
    getPlanTierDialogBackdropProps,
} from './ui/planTierPresentation';
import ActivationCodeDialog from './ui/ActivationCodeDialog';

export default function MandatoryPackageDialog({ open, expiredDate, onActivationRedeemed, onDismiss }) {
    const theme = useTheme();
    const localStoragePlanId = localStorage.getItem('packageDetails');
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [buttonLoadingIndex, setButtonLoadingIndex] = useState(null);
    const [activationDialogOpen, setActivationDialogOpen] = useState(false);
    const navigate = useNavigate();
    const { signOut } = useAuth();
    const { setUserId, setPackageDetails } = useAppContext();

    const handleLogout = async () => {
        try {
            setUserId(null);
            setPackageDetails(null);
            await signOut();
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const handleContact = () => {
        window.location.href = 'mailto:contracts@studyak.ai?subject=StudyAK%20plan%20enquiry';
    };

    useEffect(() => {
        if (!open) {
            return;
        }
        setLoading(true);
        fetchInstructorPaywallPackages()
            .then((data) => {
                setPackages(data);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching package details:', error);
                setLoading(false);
            });
    }, [open]);

    async function handleUserPlan(pkg, index, selection = {}) {
        setButtonLoadingIndex(index);
        const pricing = resolvePlanPricingDisplay(pkg, {
            billingPeriod: selection.billingPeriod || 'monthly',
        });
        const name = formatPlanLabel(pkg.name || pkg.tier);
        const tier = pkg.tier || name;
        const email = localStorage.getItem('email');

        localStorage.setItem('studyak_plan_tier', tier);
        localStorage.setItem('packageIdplan', tier);
        localStorage.setItem('name', name);

        try {
            if (pricing.mode === 'contact') {
                window.location.href = 'mailto:contracts@studyak.ai?subject=StudyAK%20plan%20enquiry';
                return;
            }

            if (pricing.mode === 'free') {
                window.location.reload();
                return;
            }

            localStorage.setItem('packagePrice', String(pricing.checkoutPrice));
            const paymentComponent = new PaymentComponent();
            await paymentComponent.makePayment(tier, selection.billingPeriod || 'monthly');
        } catch (error) {
            console.error('Plan purchase error:', error);
        } finally {
            setButtonLoadingIndex(null);
        }
    }

    const currentTier = localStorage.getItem('studyak_plan_tier');

    const sortedPkgs = sortPlansByTier(
        packages.filter((pkg) => tierRank(pkg.tier) >= 0),
    );
    const upgradeablePkgs = filterUpgradeablePlans(packages, currentTier);
    const recommendedId = currentTier && upgradeablePkgs.length
        ? getRecommendedUpgradePlanId(currentTier, upgradeablePkgs)
        : getRecommendedPlanId(sortedPkgs);

    const prevPlan = localStorage.getItem('name');
    const hasPaidPlan = localStoragePlanId && localStoragePlanId !== '1' && prevPlan && prevPlan.toLowerCase() !== 'trial';
    const expiryParsed = expiredDate ? new Date(expiredDate) : null;
    const isExpired = !!(hasPaidPlan && expiryParsed && !isNaN(expiryParsed.getTime()));
    const expiredDateLabel = isExpired
        ? expiryParsed.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
        : '';

    const getCtaLabel = (pkg, { pricingMode, isHighlighted }) => {
        const label = formatPlanLabel(pkg.name || pkg.tier);
        if (pricingMode === 'contact') {
            return 'Contact sales';
        }
        if (pricingMode === 'free') {
            return `Continue on ${label}`;
        }
        if (isHighlighted) {
            return `Reactivate ${label}`;
        }
        return `Upgrade to ${label}`;
    };

    return (
        <Dialog
            open={open}
            onClose={() => { if (onDismiss) onDismiss(); }}
            disableEscapeKeyDown={!onDismiss}
            maxWidth={false}
            PaperProps={planTierDialogPaperProps}
            slotProps={{ backdrop: getPlanTierDialogBackdropProps(theme) }}
        >
            <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
                <div style={{ position: 'relative' }}>
                {onDismiss && (
                    <button
                        type="button"
                        onClick={onDismiss}
                        aria-label="Close"
                        style={{
                            position: 'absolute',
                            top: 14,
                            right: 14,
                            zIndex: 3,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 30,
                            height: 30,
                            padding: 0,
                            borderRadius: 8,
                            border: 'none',
                            background: 'rgba(0,0,0,0.06)',
                            color: theme?.textMuted || '#6b7280',
                            cursor: 'pointer',
                        }}
                    >
                        <X size={16} />
                    </button>
                )}
                <Sheet>
                    <Head>
                        {isExpired ? (
                            <Chip $expired><span className="dot" />Subscription expired · {expiredDateLabel}</Chip>
                        ) : (
                            <Chip><Sparkles size={12} />Choose your plan</Chip>
                        )}
                        <Title>{isExpired ? 'Pick a plan to keep teaching' : 'Choose a plan to continue'}</Title>
                        <Sub>
                            {isExpired ? (
                                <>
                                    {prevPlan ? <>Your <b>{prevPlan}</b> plan has ended. </> : <>Your plan has ended. </>}
                                    All your courses, assessments, and learner data are safe — just reactivate to continue. You can change plans any time.
                                </>
                            ) : (
                                <>Pick a plan below to start building courses and assessments with StudyAK. You can change plans any time.</>
                            )}
                        </Sub>
                    </Head>

                    <ActivationCodeEntryLink onClick={() => setActivationDialogOpen(true)} />

                    {loading ? (
                        <PlanTierGridShimmer count={5} />
                    ) : sortedPkgs.length === 0 ? (
                        <EmptyState>
                            {packages.length === 0
                                ? 'Unable to load plans. Please try again later or contact support.'
                                : 'No plans available. Please contact support.'}
                        </EmptyState>
                    ) : (
                        <PlanTierGrid
                            packages={sortedPkgs}
                            recommendedId={recommendedId}
                            buttonLoadingIndex={buttonLoadingIndex}
                            onSelectPlan={handleUserPlan}
                            getCtaLabel={getCtaLabel}
                            currentTier={currentTier}
                            disableCurrentTier={false}
                            disablePastTiers={false}
                        />
                    )}

                    <PlanTierTrustStrip />

                    <Foot>
                        {onDismiss && <FootLink onClick={onDismiss}>Maybe later</FootLink>}
                        <FootLink onClick={handleContact}><Send size={13} />Talk to sales</FootLink>
                        <FootLink onClick={handleLogout}><LogOut size={13} />Sign out</FootLink>
                    </Foot>
                </Sheet>
                </div>
            </DialogContent>

            <ActivationCodeDialog
                open={activationDialogOpen}
                onClose={() => setActivationDialogOpen(false)}
                onSuccess={() => {
                    setActivationDialogOpen(false);
                    localStorage.setItem('planExpire', '0');
                    if (onActivationRedeemed) {
                        onActivationRedeemed();
                    } else {
                        window.location.reload();
                    }
                }}
            />
        </Dialog>
    );
}
